import React, { useEffect, useMemo, useState } from "react";

function card(title, content) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
          color: "#6b7280",
          marginBottom: 10,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {title}
      </div>
      {content}
    </div>
  );
}

function Field({ label, value, onChange, textarea = false, disabled = false }) {
  const commonStyle = {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 14,
    outline: "none",
    background: disabled ? "#f9fafb" : "#ffffff",
    color: "#111827",
    boxSizing: "border-box",
  };

  return (
    <label style={{ display: "grid", gap: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{label}</div>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          rows={4}
          style={{ ...commonStyle, resize: "vertical", minHeight: 96 }}
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          style={commonStyle}
        />
      )}
    </label>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 12,
        background: "#ffffff",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{value || "-"}</div>
    </div>
  );
}

function getTokenFromPath() {
  const parts = String(window.location.pathname || "").split("/").filter(Boolean);
  const idx = parts.indexOf("rec-rodina");
  if (idx === -1) return "";
  return String(parts[idx + 1] || "").trim();
}

function getFamilyStatusLabel(status) {
  const s = String(status || "").trim();
  if (s === "neodeslano") return "Neodesláno";
  if (s === "pripraveno_k_odeslani") return "Připraveno k odeslání";
  if (s === "potvrzeno_rodinou") return "Potvrzeno rodinou";
  if (s === "uzamceno") return "Uzamčeno";
  return s || "-";
}

export default function Pohrebka2RecPublicPage() {
  const token = useMemo(() => getTokenFromPath(), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!token) {
        setError("Chybí token sdílení.");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`/api/pohrebka2/rec/public/${token}`, {
          method: "GET",
          credentials: "include",
        });
        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || "Nepodařilo se načíst sdílené podklady.");
        }

        if (!cancelled) {
          setData(json.data || null);
          setError("");
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Chyba načtení.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const rec = data?.rec || {};
  const confirmed =
    rec?.potvrzenoRodinou === true ||
    rec?.stavRodina === "potvrzeno_rodinou" ||
    rec?.stavRodina === "uzamceno";

  const locked =
    rec?.uzamcenoPoPotvrzeni === true ||
    rec?.potvrzenoRodinou === true ||
    rec?.stavRodina === "potvrzeno_rodinou" ||
    rec?.stavRodina === "uzamceno";

  function patchGroup(group, key, value) {
    setData((prev) => ({
      ...(prev || {}),
      rec: {
        ...(prev?.rec || {}),
        [group]: {
          ...((prev?.rec || {})[group] || {}),
          [key]: value,
        },
      },
    }));
    setSavedMsg("");
  }

  const dalsiHudbaList = Array.isArray(data?.rec?.hudba?.dalsiSeznam)
    ? data.rec.hudba.dalsiSeznam
    : (String(data?.rec?.hudba?.dalsi || "").trim() ? [data.rec.hudba.dalsi] : []);

  function setDalsiHudbaList(nextList) {
    const normalized = (Array.isArray(nextList) ? nextList : []).map((x) => String(x || ""));
    setData((prev) => ({
      ...(prev || {}),
      rec: {
        ...(prev?.rec || {}),
        hudba: {
          ...((prev?.rec || {}).hudba || {}),
          dalsiSeznam: normalized,
          dalsi: normalized.map((x) => String(x || "").trim()).filter(Boolean).join("\n"),
        },
      },
    }));
    setSavedMsg("");
  }

  function addDalsiHudba() {
    setDalsiHudbaList([...(dalsiHudbaList || []), ""]);
  }

  function updateDalsiHudbaItem(index, value) {
    const next = [...dalsiHudbaList];
    next[index] = value;
    setDalsiHudbaList(next);
  }

  function removeDalsiHudbaItem(index) {
    const next = dalsiHudbaList.filter((_, i) => i !== index);
    setDalsiHudbaList(next);
  }

  function patchTop(key, value) {
    setData((prev) => ({
      ...(prev || {}),
      rec: {
        ...(prev?.rec || {}),
        [key]: value,
      },
    }));
    setSavedMsg("");
  }

  async function handleSave() {
    if (!token || locked || !data) return;

    setSaving(true);
    setSavedMsg("");
    try {
      const res = await fetch(`/api/pohrebka2/rec/public/${token}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          kontaktRodinaJmeno: data?.rec?.kontaktRodinaJmeno || "",
          kontaktRodinaEmail: data?.rec?.kontaktRodinaEmail || "",
          rodina: data?.rec?.rodina || {},
          zivot: data?.rec?.zivot || {},
          charakter: data?.rec?.charakter || {},
          citlive: data?.rec?.citlive || {},
          forma: data?.rec?.forma || {},
          text: data?.rec?.text || {},
        }),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Nepodařilo se uložit změny.");
      }

      setData(json.data || data);
      setSavedMsg("Změny byly uloženy.");
      setError("");
    } catch (e) {
      setError(e?.message || "Chyba při ukládání.");
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirm() {
    if (!token || locked || !data) return;

    setConfirming(true);
    setSavedMsg("");
    try {
      const res = await fetch(`/api/pohrebka2/rec/public/${token}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          kontaktRodinaJmeno: data?.rec?.kontaktRodinaJmeno || "",
          kontaktRodinaEmail: data?.rec?.kontaktRodinaEmail || "",
          rodina: data?.rec?.rodina || {},
          zivot: data?.rec?.zivot || {},
          charakter: data?.rec?.charakter || {},
          citlive: data?.rec?.citlive || {},
          forma: data?.rec?.forma || {},
          text: data?.rec?.text || {},
          potvrdit: true,
        }),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Nepodařilo se potvrdit podklady.");
      }

      setData(json.data || data);
      setSavedMsg("Podklady byly potvrzeny.");
      setError("");
    } catch (e) {
      setError(e?.message || "Chyba při potvrzení.");
    } finally {
      setConfirming(false);
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc", padding: 24 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", fontSize: 16, fontWeight: 700, color: "#111827" }}>
          Načítám podklady…
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc", padding: 24 }}>
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            background: "#ffffff",
            border: "1px solid #fecaca",
            color: "#991b1b",
            borderRadius: 16,
            padding: 18,
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: 24 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gap: 18 }}>
        <div
          style={{
            background: "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
            color: "#ffffff",
            borderRadius: 20,
            padding: 24,
            boxShadow: "0 16px 40px rgba(15,23,42,0.18)",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 800, opacity: 0.82, marginBottom: 8 }}>
            Podklady k řeči
          </div>
          <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1.15 }}>
            {data?.zesnuly || "Rodinné podklady"}
          </div>
          <div style={{ marginTop: 10, fontSize: 15, opacity: 0.9, lineHeight: 1.5 }}>
            Tady můžete doplnit informace pro řečnici. Po potvrzení se podklady uzavřou.
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <SummaryItem label="Datum obřadu" value={data?.obrad?.datum} />
          <SummaryItem label="Čas obřadu" value={data?.obrad?.cas} />
          <SummaryItem label="Místo obřadu" value={data?.obrad?.misto} />
          <SummaryItem label="Řečník / řečnice" value={data?.obrad?.recnik} />
          <SummaryItem label="Stav pro rodinu" value={getFamilyStatusLabel(rec?.stavRodina)} />
          <SummaryItem label="Potvrzeno" value={confirmed ? "Ano" : "Ne"} />
        </div>

        {error ? (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              borderRadius: 14,
              padding: 14,
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        ) : null}

        {savedMsg ? (
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #86efac",
              color: "#166534",
              borderRadius: 14,
              padding: 14,
              fontWeight: 700,
            }}
          >
            {savedMsg}
          </div>
        ) : null}

        {confirmed ? (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #93c5fd",
              color: "#1d4ed8",
              borderRadius: 14,
              padding: 14,
              fontWeight: 700,
            }}
          >
            Podklady byly potvrzeny a formulář je uzamčen.
          </div>
        ) : null}

        {locked && !confirmed ? (
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fcd34d",
              color: "#92400e",
              borderRadius: 14,
              padding: 14,
              fontWeight: 700,
            }}
          >
            Tyto podklady jsou už uzamčené a nelze je dál měnit.
          </div>
        ) : null}

        <div style={{ display: "grid", gap: 16 }}>
          {card(
            "Kontakt",
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <Field label="Jméno kontaktu" value={rec?.kontaktRodinaJmeno} onChange={(v) => patchTop("kontaktRodinaJmeno", v)} disabled={locked} />
              <Field label="E-mail" value={rec?.kontaktRodinaEmail} onChange={(v) => patchTop("kontaktRodinaEmail", v)} disabled={locked} />
            </div>
          )}

          {card(
            "Rodina",
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <Field label="Partner / partnerka" value={rec?.rodina?.partner} onChange={(v) => patchGroup("rodina", "partner", v)} disabled={locked} />
              <Field label="Děti" value={rec?.rodina?.deti} onChange={(v) => patchGroup("rodina", "deti", v)} disabled={locked} />
              <Field label="Vnoučata" value={rec?.rodina?.vnucata} onChange={(v) => patchGroup("rodina", "vnucata", v)} disabled={locked} />
              <Field label="Sourozenci" value={rec?.rodina?.sourozenci} onChange={(v) => patchGroup("rodina", "sourozenci", v)} disabled={locked} />
              <Field label="Rodiče" value={rec?.rodina?.rodice} onChange={(v) => patchGroup("rodina", "rodice", v)} disabled={locked} />
              <Field label="Další blízcí" value={rec?.rodina?.dalsi} onChange={(v) => patchGroup("rodina", "dalsi", v)} disabled={locked} />
            </div>
          )}

          {card(
            "Život",
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <Field label="Původ" value={rec?.zivot?.puvod} onChange={(v) => patchGroup("zivot", "puvod", v)} disabled={locked} />
              <Field label="Dětství" value={rec?.zivot?.detstvi} onChange={(v) => patchGroup("zivot", "detstvi", v)} disabled={locked} />
              <Field label="Škola" value={rec?.zivot?.skola} onChange={(v) => patchGroup("zivot", "skola", v)} disabled={locked} />
              <Field label="Práce" value={rec?.zivot?.prace} onChange={(v) => patchGroup("zivot", "prace", v)} disabled={locked} />
              <Field label="Zájmy" value={rec?.zivot?.zajmy} onChange={(v) => patchGroup("zivot", "zajmy", v)} disabled={locked} />
              <Field label="Hodnoty" value={rec?.zivot?.hodnoty} onChange={(v) => patchGroup("zivot", "hodnoty", v)} disabled={locked} />
            </div>
          )}

          {card(
            "Charakter a vzpomínky",
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Povaha" value={rec?.charakter?.povaha} onChange={(v) => patchGroup("charakter", "povaha", v)} textarea disabled={locked} />
              <Field label="Vlastnosti" value={rec?.charakter?.vlastnosti} onChange={(v) => patchGroup("charakter", "vlastnosti", v)} textarea disabled={locked} />
              <Field label="Zvyky" value={rec?.charakter?.zvyky} onChange={(v) => patchGroup("charakter", "zvyky", v)} textarea disabled={locked} />
              <Field label="Vzpomínky" value={rec?.charakter?.vzpominky} onChange={(v) => patchGroup("charakter", "vzpominky", v)} textarea disabled={locked} />
              <Field label="Určitě zmínit" value={rec?.charakter?.urciteZminit} onChange={(v) => patchGroup("charakter", "urciteZminit", v)} textarea disabled={locked} />
            </div>
          )}

          {card(
            "Citlivé body",
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Nezmiňovat" value={rec?.citlive?.nezminovat} onChange={(v) => patchGroup("citlive", "nezminovat", v)} textarea disabled={locked} />
              <Field label="Zmínit opatrně" value={rec?.citlive?.opatrne} onChange={(v) => patchGroup("citlive", "opatrne", v)} textarea disabled={locked} />
              <Field label="Poznámka" value={rec?.citlive?.poznamka} onChange={(v) => patchGroup("citlive", "poznamka", v)} textarea disabled={locked} />
            </div>
          )}

          {card(
            "Forma řeči",
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <Field label="Délka" value={rec?.forma?.delka} onChange={(v) => patchGroup("forma", "delka", v)} disabled={locked} />
              <Field label="Styl" value={rec?.forma?.styl} onChange={(v) => patchGroup("forma", "styl", v)} disabled={locked} />
              <Field label="Náboženský tón" value={rec?.forma?.nabozenskyTon} onChange={(v) => patchGroup("forma", "nabozenskyTon", v)} disabled={locked} />
              <Field label="Důležitý důraz" value={rec?.forma?.dulezityDuraz} onChange={(v) => patchGroup("forma", "dulezityDuraz", v)} disabled={locked} />
              <Field label="Závěr" value={rec?.forma?.zaver} onChange={(v) => patchGroup("forma", "zaver", v)} disabled={locked} />
            </div>
          )}

          {card(
            "Hudba k obřadu",
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
                <Field label="Nástup" value={rec?.hudba?.nastup} onChange={(v) => patchGroup("hudba", "nastup", v)} disabled={locked} />
                <Field label="Před proslovem" value={rec?.hudba?.predProslovem} onChange={(v) => patchGroup("hudba", "predProslovem", v)} disabled={locked} />
                <Field label="Po proslovu / závěr" value={rec?.hudba?.zaver} onChange={(v) => patchGroup("hudba", "zaver", v)} disabled={locked} />
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>Další hudba</div>

                <div style={{ display: "grid", gap: 10 }}>
                  {dalsiHudbaList.map((item, index) => (
                    <div
                      key={`public-dalsi-hudba-${index}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 10,
                        alignItems: "start",
                      }}
                    >
                      <Field
                        label={`Další hudba ${index + 1}`}
                        value={item}
                        onChange={(v) => updateDalsiHudbaItem(index, v)}
                        disabled={locked}
                      />
                      <button
                        type="button"
                        onClick={() => removeDalsiHudbaItem(index)}
                        disabled={locked}
                        style={{
                          marginTop: 28,
                          border: "1px solid #d1d5db",
                          background: locked ? "#f3f4f6" : "#fff",
                          color: "#111827",
                          borderRadius: 12,
                          padding: "12px 14px",
                          fontWeight: 700,
                          cursor: locked ? "not-allowed" : "pointer",
                          opacity: locked ? 0.6 : 1,
                        }}
                      >
                        Odebrat
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={addDalsiHudba}
                    disabled={locked}
                    style={{
                      border: "1px solid #d1d5db",
                      background: locked ? "#f3f4f6" : "#fff",
                      color: "#111827",
                      borderRadius: 12,
                      padding: "12px 14px",
                      fontWeight: 700,
                      cursor: locked ? "not-allowed" : "pointer",
                      opacity: locked ? 0.6 : 1,
                    }}
                  >
                    + Přidat další hudbu
                  </button>
                </div>
              </div>
            </div>
          )}

          {card(
            "Doplňující text",
            <div style={{ display: "grid", gap: 12 }}>
              <Field label="Poznámky" value={rec?.text?.poznamky} onChange={(v) => patchGroup("text", "poznamky", v)} textarea disabled={locked} />
              <Field label="Návrh řeči" value={rec?.text?.navrhReci} onChange={(v) => patchGroup("text", "navrhReci", v)} textarea disabled={locked} />
            </div>
          )}
        </div>

        {!locked ? (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", paddingBottom: 24 }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                border: "none",
                borderRadius: 14,
                padding: "14px 18px",
                fontWeight: 800,
                fontSize: 15,
                background: "#111827",
                color: "#ffffff",
                cursor: "pointer",
              }}
            >
              {saving ? "Ukládám..." : "Uložit změny"}
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirming}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 14,
                padding: "14px 18px",
                fontWeight: 800,
                fontSize: 15,
                background: "#ffffff",
                color: "#111827",
                cursor: "pointer",
              }}
            >
              {confirming ? "Potvrzuji..." : "Potvrdit podklady"}
            </button>
          </div>
        ) : (
          <div style={{ paddingBottom: 24 }} />
        )}
      </div>
    </div>
  );
}
